// @ts-nocheck
import { EntityLink } from "@/components/shared/EntityLink";
import React, { useState, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Pencil,
  Trash2,
  Plus,
  Download,
  Upload,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { validateExcelFile, extractErrorMessage } from "@/lib/utils";
import type { CtpPlateLog, CtpPlateLogFormData } from "@/shared/schemas";

import CtpPlateLogForm from "./CtpPlateLogForm";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useConfirm } from "@/providers/ConfirmProvider";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";

const CTP_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "logDate", label: "Ngày", width: 14, format: "date" },
  { key: "plateType", label: "Tên Kẽm", width: 16 },
  { key: "productName", label: "Sản Phẩm", width: 34 },
  { key: "poReference", label: "Số PO", width: 18 },
  { key: "importQty", label: "Nhập", width: 10, type: "number" },
  { key: "usedQty", label: "Xuất", width: 10, type: "number" },
  { key: "stockQty", label: "Tồn", width: 10, type: "number" },
  { key: "notes", label: "Ghi Chú", width: 30 },
];
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";

import { toVnDateString } from "@apv-erp/status";
const FILTER_FIELDS: FilterField[] = [
  {
    key: "productName",
    label: "Tên Sản Phẩm",
    type: "text",
    placeholder: "Tên SP...",
  },
  {
    key: "poReference",
    label: "Số Kiện (PO)",
    type: "text",
    placeholder: "PO-...",
  },
  {
    key: "type",
    label: "Trạng thái",
    type: "combobox-multi",
    options: [
      { value: "NHAP", label: "Nhập kẽm" },
      { value: "XUAT", label: "Xuất sử dụng" },
    ],
  },
  {
    key: "plateType",
    label: "Tên kẽm",
    type: "combobox-multi",
    options: [
      { value: "Kẽm TA", label: "Kẽm TA" },
      { value: "Kẽm Kodak", label: "Kẽm Kodak" },
      { value: "Kẽm UV", label: "Kẽm UV" },
      { value: "Kẽm Khác", label: "Kẽm Khác" },
    ],
  },
  { key: "logDate", label: "Thời gian", type: "date-range" },
];

export default function CtpPlateLogList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<CtpPlateLog | null>(null);
  const [viewItem, setViewItem] = useState<CtpPlateLog | null>(null);
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();

  const {
    paginatedItems,
    isLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    filtered,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
  } = useServerListFilter<CtpPlateLog>({
    queryKey: "ctp-logs-all",
    queryFn: (params) =>
      trpcClient.technical.ctpLogs.list.query({
        ...params,
        logDateFrom: dateParams.from || params.logDateFrom,
        logDateTo: dateParams.to || params.logDateTo,
      }) as any,
  });

  // Kho kẽm (phòng CTP quản lý): tồn hiện tại theo khổ kẽm, máy chủ tự tính nhập − xuất
  const { data: stockSummary = [] } = useQuery({
    queryKey: ["ctp-stock-summary"],
    queryFn: () => trpcClient.technical.ctpLogs.stockSummary.query() as Promise<any[]>,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      trpcClient.technical.ctpLogs.delete.mutate({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ctp-logs-all"] });
      queryClient.invalidateQueries({ queryKey: ["ctp-stock-summary"] });
    },
    onError: () => {
      toast.error("Lỗi khi xóa nhật ký");
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CtpPlateLogFormData) =>
      trpcClient.technical.ctpLogs.create.mutate(data),
    onSuccess: () => {
      setIsCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["ctp-logs-all"] });
      queryClient.invalidateQueries({ queryKey: ["ctp-stock-summary"] });
    },
    onError: (err: any) => toast.error("Có lỗi xảy ra: " + extractErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CtpPlateLogFormData }) =>
      trpcClient.technical.ctpLogs.update.mutate({ id, data }),
    onSuccess: () => {
      setEditItem(null);
      queryClient.invalidateQueries({ queryKey: ["ctp-logs-all"] });
      queryClient.invalidateQueries({ queryKey: ["ctp-stock-summary"] });
    },
    onError: (err: any) => toast.error("Có lỗi xảy ra: " + extractErrorMessage(err)),
  });

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();
  const rowIds = React.useMemo(() => (filtered || []).map((l: any) => l.id), [filtered]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const handleExportAll = () => exportToExcel(filtered || [], CTP_EXPORT_COLUMNS, "nhat_ky_kem_ctp");
  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: "Xuất Excel",
          variant: "outline",
          onClick: () =>
            exportToExcel(
              (filtered || []).filter((l: any) => selectedIds.has(l.id)),
              CTP_EXPORT_COLUMNS,
              "nhat_ky_kem_ctp_da_chon",
            ),
        },
        ...(hasPermission("plates", "delete")
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: "destructive" as const,
                onClick: () =>
                  executeBulkAction({
                    confirmFn: confirm,
                    selectedIds: Array.from(selectedIds),
                    actionFn: async (id) => trpcClient.technical.ctpLogs.delete.mutate({ id }),
                    actionName: "xóa",
                    itemType: "nhật ký kẽm",
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: ["ctp-logs-all"] });
      queryClient.invalidateQueries({ queryKey: ["ctp-stock-summary"] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} nhật ký kẽm CTP đã chọn?`,
                  }),
              },
            ]
          : []),
      ]
    : [];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateExcelFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const XLSX = await import("xlsx");
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
      } catch (err) {
        toast.error("Lỗi đọc file Excel: " + extractErrorMessage(err));
      }
    };
    reader.readAsBinaryString(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      <input
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileUpload}
      />
      <ListPageShell
        unitLabel="Nhật ký Kẽm CTP"
        forceTwoRows
        scopeLabel={
          <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-slate-100 border border-slate-200 text-xs font-semibold text-slate-700 whitespace-nowrap">
            Nhật ký xuất &amp; thu hồi kẽm CTP ({totalItems})
          </span>
        }
        topBanner={
          stockSummary.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 text-xs px-1 py-1.5" data-testid="ctp-stock-summary">
              <span className="text-slate-500">Tồn kẽm:</span>
              {stockSummary.map((g: any) => (
                <span
                  key={g.plateType}
                  className={`inline-flex items-center h-6 px-2 rounded-md border font-semibold ${
                    g.stock < 0 ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"
                  }`}
                  title={`Nhập ${g.imported} · Xuất ${g.used}`}
                >
                  {g.plateType}: {g.stock} lá
                </span>
              ))}
            </div>
          ) : undefined
        }
        onExport={handleExportAll}
        secondaryActions={[
          {
            key: "sub-ledger-entry",
            label: "Ghi sổ phụ",
            icon: <Plus className="h-3.5 w-3.5 text-slate-600" />,
            onClick: () => setIsCreateOpen(true),
          },
          {
            key: "import-ctp-excel",
            label: "Nhập Excel",
            icon: <Upload className="h-3.5 w-3.5 text-emerald-700" />,
            onClick: () => fileInputRef.current?.click(),
          },
        ]}
        totalItems={totalItems}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={showFilter}
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        dateFilter={
          <DatePeriodFilter
            period={period}
            setPeriod={setPeriod}
            customFrom={customFrom}
            setCustomFrom={setCustomFrom}
            customTo={customTo}
            setCustomTo={setCustomTo}
          />
        }
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        actions={
          // Kẽm về: ghi Nhập vào sổ (tăng tồn); xuất kẽm tự ghi khi Ghi bản kẽm xong ở lệnh / ghi bù
          <Button
            size="sm"
            onClick={() => setIsCreateOpen(true)}
            className="h-7 px-2.5 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shrink-0"
            data-testid="ctp-import-plates"
          >
            <Plus className="w-3.5 h-3.5 shrink-0" />
            <span>Nhập kẽm</span>
          </Button>
        }
      >
        <div className="overflow-x-auto min-h-[50vh]">
          <Table className={`w-full text-xs caption-bottom apv-compact-table ${rowSelection.selectionMode ? "apv-select-mode" : ""}`}>
            <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-sm apv-table-header">
              <TableRow className="border-b h-10">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <TableHead className="px-3 py-2 text-left font-medium text-slate-500 w-[120px] uppercase">
                  Ngày / Tên Kẽm
                </TableHead>
                <TableHead className="px-3 py-2 text-left font-medium text-slate-500 uppercase">
                  Sản Phẩm & PO
                </TableHead>
                <TableHead className="px-3 py-2 text-center font-medium text-slate-500 w-[200px] uppercase">
                  Nhập / Xuất / Tồn
                </TableHead>
                <TableHead className="px-3 py-2 text-left font-medium text-slate-500 uppercase">
                  Ghi Chú
                </TableHead>
                <TableHead className="apv-col-actions w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-slate-100 bg-white">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6} className="p-3">
                        <div className="h-6 bg-slate-100 rounded animate-pulse" />
                      </TableCell>
                    </TableRow>
                  ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-12 text-slate-400"
                  >
                    Không tìm thấy dữ liệu
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((log: CtpPlateLog) => (
                  <DataTableRow
                    key={log.id}
                    id={log.id}
                    rowSelection={rowSelection}
                    className="hover:bg-slate-50 transition-colors group cursor-pointer"
                    onClick={() => setViewItem(log)}
                  >
                    <DataTableRowCheckbox id={log.id} rowSelection={rowSelection} />
                    <TableCell className="apv-col-code px-3 py-1.5">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono font-bold text-[12px] text-slate-700 leading-none">
                          {log.logDate
                            ? format(new Date(log.logDate), "dd/MM/yyyy", {
                                locale: vi,
                              })
                            : ""}
                        </span>
                        <span className="text-[10px] px-1.5 py-0 w-fit rounded-full bg-slate-100 text-slate-700 border font-medium leading-tight">
                          {log.plateType || "Chưa rõ"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-1.5">
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold text-slate-900 text-[12px] leading-none truncate" title={log.productName}>
                          {log.productName || "—"}
                        </div>
                        <div className="font-mono font-bold text-[10px] text-slate-500 uppercase leading-none tracking-tighter">
                          PO: {log.poReference || "-"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <div className="flex flex-col items-center">
                          <span className="text-[9px] text-slate-400 uppercase">
                            Nhập
                          </span>
                          {log.importQty > 0 ? (
                            <span className="text-[12px] font-bold text-emerald-600 leading-none">
                              +{log.importQty}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-300">-</span>
                          )}
                        </div>
                        <div className="flex flex-col items-center">
                          <span className="text-[9px] text-slate-400 uppercase">
                            Xuất
                          </span>
                          {log.usedQty > 0 ? (
                            <span className="text-[12px] font-bold text-rose-600 leading-none">
                              -{log.usedQty}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-300">-</span>
                          )}
                        </div>
                        <div className="flex flex-col items-center pl-2 border-l">
                          <span className="text-[9px] text-slate-400 uppercase">
                            Tồn
                          </span>
                          <span className="text-[12px] font-bold text-indigo-700 leading-none">
                            {log.stockQty}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-1.5">
                      <span
                        className="text-[11px] text-slate-500 line-clamp-2 leading-tight max-w-[250px]"
                        title={log.notes || ""}
                      >
                        {log.notes || "-"}
                      </span>
                    </TableCell>
                    <TableCell
                      className="apv-col-actions px-2 py-1.5 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                            title="Xóa nhật ký"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="z-[10000]">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Xác nhận xoá</AlertDialogTitle>
                            <AlertDialogDescription>
                              Bạn có chắc muốn xoá nhật ký CTP của "
                              {log.productName}"?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              onClick={(e) => e.stopPropagation()}
                            >
                              Huỷ
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMutation.mutate(log.id);
                              }}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Xoá
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </DataTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[500px] z-[10000]">
          <DialogHeader>
            <DialogTitle>Thêm Nhật Ký CTP</DialogTitle>
            <DialogDescription className="sr-only">Nhập thông tin sử dụng kẽm.</DialogDescription>
          </DialogHeader>
          <CtpPlateLogForm
            onSubmit={async (data) => {
              await createMutation.mutateAsync(data);
            }}
            onCancel={() => setIsCreateOpen(false)}
            submitLabel="Thêm mới"
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editItem}
        onOpenChange={(open) => !open && setEditItem(null)}
      >
        <DialogContent className="sm:max-w-[500px] z-[10000]">
          <DialogHeader>
            <DialogTitle>Cập nhật Nhật Ký CTP</DialogTitle>
          </DialogHeader>
          {editItem && (
            <CtpPlateLogForm
              defaultValues={{
                logDate: editItem.logDate
                  ? toVnDateString(editItem.logDate)
                  : "",
                productId: editItem.productId || "",
                productName: editItem.productName,
                poReference: editItem.poReference || "",
                importQty: editItem.importQty,
                usedQty: editItem.usedQty,
                stockQty: editItem.stockQty,
                plateType: editItem.plateType || "",
                notes: editItem.notes || "",
              }}
              onSubmit={async (data) => {
                await updateMutation.mutateAsync({ id: editItem.id, data });
              }}
              onCancel={() => setEditItem(null)}
              submitLabel="Cập nhật"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!viewItem}
        onOpenChange={(open) => !open && setViewItem(null)}
      >
        <DialogContent className="sm:max-w-[480px] z-[10000]">
          <DialogHeader>
            <div className="flex items-center justify-between pr-4">
              <DialogTitle>
                Chi Tiết Nhật Ký Kẽm CTP
              </DialogTitle>
              <Badge variant="outline" className="font-mono text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                {viewItem?.plateType || "Kẽm CTP"}
              </Badge>
            </div>
            <DialogDescription className="sr-only">
              Thông tin chi tiết xuất nhập tồn bản kẽm
            </DialogDescription>
          </DialogHeader>

          {viewItem && (
            <div className="space-y-3 py-2 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">Ngày ghi nhận</span>
                  <span className="font-mono font-bold text-slate-800 text-sm">
                    {viewItem.logDate ? format(new Date(viewItem.logDate), "dd/MM/yyyy", { locale: vi }) : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold">Lệnh SX / PO</span>
                  <span className="font-mono font-bold text-indigo-700 text-sm break-words">
                    {/* Tham chiếu là chữ nhập tay; mã lệnh SX thì mở được màn lệnh (route nhận mã) */}
                    {/^LSX-/.test(viewItem.poReference || "") ? (
                      <EntityLink entityType="productionOrder" id={viewItem.poReference} code={viewItem.poReference} />
                    ) : (
                      viewItem.poReference || "—"
                    )}
                  </span>
                </div>
              </div>

              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-0.5">Sản phẩm</span>
                <div className="font-semibold text-slate-900 text-sm bg-white p-2.5 rounded border border-slate-200 break-words">
                  {viewItem.productId ? (
                    <EntityLink entityType="product" id={viewItem.productId} label={viewItem.productName} />
                  ) : (
                    viewItem.productName || "—"
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-lg p-2.5">
                  <span className="text-[10px] font-bold text-emerald-700 uppercase block">Nhập kho</span>
                  <span className="text-base font-mono font-black text-emerald-700">
                    +{viewItem.importQty || 0}
                  </span>
                </div>
                <div className="bg-rose-50/70 border border-rose-200/80 rounded-lg p-2.5">
                  <span className="text-[10px] font-bold text-rose-700 uppercase block">Xuất dùng</span>
                  <span className="text-base font-mono font-black text-rose-700">
                    -{viewItem.usedQty || 0}
                  </span>
                </div>
                <div className="bg-indigo-50/70 border border-indigo-200/80 rounded-lg p-2.5">
                  <span className="text-[10px] font-bold text-indigo-700 uppercase block">Tồn kho</span>
                  <span className="text-base font-mono font-black text-indigo-700">
                    {viewItem.stockQty || 0}
                  </span>
                </div>
              </div>

              {viewItem.notes && (
                <div className="bg-slate-50 p-2.5 rounded border border-slate-100">
                  <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-0.5">Ghi chú</span>
                  <p className="text-slate-700 whitespace-pre-wrap">{viewItem.notes}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setViewItem(null)}
            >
              Đóng
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => {
                if (viewItem) {
                  setEditItem(viewItem);
                  setViewItem(null);
                }
              }}
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              Chỉnh Sửa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
